import React, { useState, useEffect, useContext } from 'react'
import moment from 'moment'
import { get, isEqual } from 'lodash'
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
} from 'antd'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

const { Option } = Select

const CollectionCreateForm = ({
  visible,
  onCreate,
  onCancel,
  loading,
  isUpdate,
  onUpdate,
  amount,
  data,
  date,
}) => {
  date = date ? moment() : moment(date)
  if (get(data, 'whichYear')) {
    data.whichYear = moment(data.whichYear.toString())
  }

  if (get(data, 'client.id')) {
    data.clientId = get(data, 'client.id')
  }

  if (get(data, 'nextDueDate')) {
    data.nextDueDate = moment(data.nextDueDate)
  }

  if (get(data, 'startDate')) {
    data.startDate = moment(data.startDate)
  }

  if (get(data, 'paidAt')) {
    data.paidAt = moment(data.paidAt)
  }

  const [form] = Form.useForm()
  const [hasChanges, setHasChanges] = useState(false)
  const [initialValues, setInitialValues] = useState(null)

  const {
    state: { currentUser, userPermissions, isClient } = {},
  } = useContext(AppContext) || {}

  const hasEditPermission = canUserEdit(userPermissions || currentUser, isClient)
  const hasAddPermission = canUserAdd(userPermissions || currentUser, isClient)
  const hasSavePermission = canUserSave(userPermissions || currentUser, isClient)

  const canSubmit = isUpdate
    ? (hasEditPermission && hasSavePermission)
    : (hasAddPermission && hasSavePermission)
  const isReadOnly = isUpdate ? !hasEditPermission : !hasAddPermission

  const whichYear = moment()
  const status = 'COMPLETED'

  function resetForm() {
    form.resetFields()
    setHasChanges(false)
    setInitialValues(null)
  }

  useEffect(() => {
    if (visible && isUpdate && data) {
      const initial = {
        date,
        status: status,
        amount: amount || data?.amount,
        whichYear: data?.whichYear
          ? moment(data.whichYear.toString())
          : whichYear,
        modifier: 'public',
        amcType: data?.amcType ? data?.amcType : 'New',
        amcQty: data?.amcQty,
        taxInvoiceNumber: data?.taxInvoiceNumber,
        startDate: data?.startDate ? moment(data.startDate) : null,
        nextDueDate: data?.nextDueDate ? moment(data.nextDueDate) : null,
        paidAt: data?.paidAt ? moment(data.paidAt) : null,
        paymentType: data?.paymentType,
        description: data?.description,
        invoiceNo: data?.invoiceNo,
        clientId: data?.client?.id || data?.clientId,
      }
      setInitialValues(initial)
      setHasChanges(false)
    } else if (visible && !isUpdate) {
      setInitialValues(null)
      setHasChanges(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isUpdate, data])

  const normalizeFormValues = (values) => {
    const normalized = {}
    Object.keys(values).forEach((key) => {
      const val = values[key]
      if (moment.isMoment(val)) {
        normalized[key] = val.format('YYYY-MM-DD')
      } else if (val === null || val === undefined) {
        normalized[key] = ''
      } else {
        normalized[key] = val
      }
    })
    return normalized
  }

  const checkFormChanges = () => {
    if (!isUpdate || !initialValues) {
      return
    }
    const currentValues = form.getFieldsValue()
    const normalizedCurrent = normalizeFormValues(currentValues)
    const normalizedInitial = normalizeFormValues(initialValues)
    const changed = !isEqual(normalizedCurrent, normalizedInitial)
    setHasChanges(changed)
  }

  const paymentTypes = [
    { text: 'CASH', value: 'CASH' },
    { text: 'CHEQUE', value: 'CHEQUE' },
    { text: 'ONLINE', value: 'ONLINE' },
  ]

  const statusTypes = [
    { text: 'PENDING', value: 'PENDING' },
    { text: 'COMPLETED', value: 'COMPLETED' },
  ]

  const amcTypes = [
    { text: 'New', value: 'New' },
    { text: 'Renewal', value: 'Renewal' },
    { text: 'Existing New', value: 'Existing New' },
  ]

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={isUpdate ? 'Update AMC' : 'Create AMC'}
      okText={isUpdate ? 'Update' : 'Create'}
      okButtonProps={{
        loading,
        disabled: (isUpdate && !hasChanges) || !canSubmit,
      }}
      cancelText='Cancel'
      onCancel={() => {
        resetForm()
        onCancel()
      }}
      onOk={() => {
        if (!canSubmit) return
        form
          .validateFields()
          .then((values) => {
            if (isUpdate) {
              onUpdate(values, resetForm)
            } else {
              onCreate(values, resetForm)
            }
          })
          .catch((info) => console.log('Validate Failed:', info))
      }}
    >
      <Form
        form={form}
        layout='vertical'
        name='form_in_modal'
        initialValues={{
          date,
          status,
          amount,
          whichYear,
          modifier: 'public',
          amcType: data?.amcType ? data?.amcType : 'New',
          ...data,
        }}
        onValuesChange={checkFormChanges}
      >
        <fieldset
          disabled={isReadOnly}
          style={{ border: 0, padding: 0, margin: 0 }}
        >
        <Form.Item
          name='amount'
          label='Amount'
          rules={[{ required: true, message: 'Please input your Amount!' }]}
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            placeholder='Enter Amount'
          />
        </Form.Item>
        <Form.Item
          name='amcQty'
          label='AMC Quantity'
          rules={[
            { required: true, message: 'Please input your AMC Quantity!' },
          ]}
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            placeholder='Enter AMC Quantity'
          />
        </Form.Item>
        <Form.Item name='taxInvoiceNumber' label='Tax Invoice Number'>
          <Input
            min={0}
            style={{ width: '100%' }}
            placeholder='Enter Tax Invoice Number'
          />
        </Form.Item>
        <Form.Item
          name='whichYear'
          label='Year Of'
          rules={[{ required: true, message: 'Please input your Which Year!' }]}
        >
          <DatePicker
            format={'DD-MMM-YYYY'}
            getPopupContainer={(trigger) => trigger.parentElement}
            picker='year'
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name='startDate'
          label='Start Date'
          rules={[
            { required: true, message: 'Please input your Next Start Date!' },
          ]}
        >
          <DatePicker
            format={'DD-MMM-YYYY'}
            getPopupContainer={(trigger) => trigger.parentElement}
            autoComplete='off'
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name='nextDueDate'
          label='Next Due Date'
          rules={[
            { required: true, message: 'Please input your Next Due Date!' },
          ]}
        >
          <DatePicker
            format={'DD-MMM-YYYY'}
            getPopupContainer={(trigger) => trigger.parentElement}
            autoComplete='off'
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name='paidAt'
          label='Paid At'
          rules={[{ required: true, message: 'Please input your Paid At!' }]}
        >
          <DatePicker
            format={'DD-MMM-YYYY'}
            getPopupContainer={(trigger) => trigger.parentElement}
            autoComplete='off'
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name='paymentType'
          label='Select Payment Type'
          rules={[
            {
              required: true,
              message: 'Please Select Payment Type!',
              whitespace: true,
            },
          ]}
        >
          <Select
            getPopupContainer={(trigger) => trigger.parentElement}
            placeholder='Select Payment Type'
          >
            {paymentTypes.map((paymentType) => (
              <Option key={paymentType.value} value={paymentType.value}>
                {paymentType.text}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name='amcType'
          label='AMC Type'
          rules={[
            {
              required: true,
              message: 'Please Select AMC Type!',
              whitespace: true,
            },
          ]}
        >
          <Select
            getPopupContainer={(trigger) => trigger.parentElement}
            placeholder='Select AMC Type'
          >
            {amcTypes.map((statusType) => (
              <Option key={statusType.value} value={statusType.value}>
                {statusType.text}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name='status'
          label='Select Status Type'
          rules={[
            {
              required: true,
              message: 'Please Select Status Type!',
              whitespace: true,
            },
          ]}
        >
          <Select
            getPopupContainer={(trigger) => trigger.parentElement}
            placeholder='Select Status Type'
          >
            {statusTypes.map((statusType) => (
              <Option key={statusType.value} value={statusType.value}>
                {statusType.text}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          hasFeedback
          name='description'
          label='Description'
        // rules={[
        //   {
        //     required: true,
        //     message: 'Please write your description!',
        //     whitespace: true,
        //   },
        // ]}
        >
          <Input.TextArea rows={5} />
        </Form.Item>
        <Form.Item name='date' style={{ display: 'none' }}>
          <DatePicker
            getPopupContainer={(trigger) => trigger.parentElement}
            format={'DD-MMM-YYYY'}
          />
        </Form.Item>
        <Form.Item
          name='invoiceNo'
          label='Invoice Number'
          rules={[
            { required: true, message: 'Please input your Invoice Number' },
          ]}
        >
          <Input
            min={0}
            style={{ width: '100%' }}
            placeholder='Enter Invoice Number'
          />
        </Form.Item>
        </fieldset>

        <div className='footer-btn-div'>
          <Button
            className='footer-cancel-btn'
            onClick={() => {
              resetForm()
              onCancel()
            }}
          >
            Cancel
          </Button>
          <Button
            className='footer-submit-btn'
            onClick={() => {
              if (!canSubmit) return
              form
                .validateFields()
                .then((values) => {
                  if (isUpdate) {
                    onUpdate(values, resetForm)
                  } else {
                    onCreate(values, resetForm)
                  }
                })
                .catch((info) => console.log('Validate Failed:', info))
            }}
            type='primary'
            loading={loading}
            disabled={(isUpdate && !hasChanges) || !canSubmit}
          >
            {isUpdate ? 'Update' : 'Create'}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

const CreateTicketModal = (props) => {
  return (
    <div>
      <CollectionCreateForm {...props} />
    </div>
  )
}

export default CreateTicketModal